import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import { ReactSVG } from 'react-svg';
import { iconList } from 'assets';
import { isLogin } from 'api';
import { Button, Checkbox, Col, Field, Heading, Row, Password } from 'components';
import {
  GoogleButton,
  MicrosoftButton,
  FacebookButton,
  LinkedinButton,
  TwitterButton,
  AppleButton,
} from 'containers/auth/Login/Login.styled';

import { loginUserCreator, setNewAlertCreator, loginSocialCreator } from 'store/actionsCreators';
import { HelpCenter } from '../../../shared';

function Login({ loginUser, loginSocial, setNewAlert, location, history }) {
  const { t } = useTranslation();
  const [helpCenter, setHelpCenter] = useState(null);

  const showActivationAlert = () => {
    setNewAlert({
      type: 'auth',
      status: 'success',
      text: t('auth.accountVerified'),
    });
  };

  const showAlreadyEnabledAlert = () => {
    setNewAlert({
      type: 'auth',
      status: 'success',
      text: t('auth.accountAlreadyEnabled'),
    });
  };

  useEffect(() => {
    if (isLogin()) {
      history.replace('/');
    }
    window.localStorage.removeItem('ipmaShopVerify');
    const queryParams = new URLSearchParams(location.search);
    const confirmedParam = queryParams.has('confirmed') && queryParams.get('confirmed') === 'true';
    const enabledParam = queryParams.has('enabled') && queryParams.get('enabled') === 'true';

    if (confirmedParam) showActivationAlert();
    if (enabledParam) showAlreadyEnabledAlert();
  }, []);

  const [form, setForm] = useState({
    username: '',
    password: '',
    remember: false,
  });

  const handleChange = (name) => (e) => {
    e.preventDefault();
    setForm({ ...form, [name]: e.target.value });
  };

  const handleChangeCustom = (name) => (value) => {
    setForm({ ...form, [name]: value });
  };

  const submit = (e) => {
    e.preventDefault();
    loginUser({ username: form.username, password: form.password }, history, false);
  };

  return (
    <div>
      <HelpCenter openType={helpCenter} closeFn={() => setHelpCenter(null)} />
      <Row halign="justify" valign="center">
        <Col size={{ xs: 3 / 4 }}>
          <Heading as="h1" size="h2">
            {t('auth.logIn')}
          </Heading>
          <Margin as="p" top={{ xs: 20 }}>
            {t('auth.dontHaveAccount')}{' '}
            <Link className="link" to="/auth/register">
              {t('auth.toRegister')}
            </Link>
          </Margin>
          <Margin as="p" top={{ xs: 20 }}>
            {t('auth.needHelp')}{' '}
            <div className="link cursor-hover-pointer" onClick={() => setHelpCenter('auth')}>
              {t('auth.toHelpCenter')}
            </div>
          </Margin>
        </Col>
      </Row>
      <Row>
        <Col size={{ lg: 1 / 2 }}>
          <form onSubmit={submit}>
            <Margin top={{ xs: 28 }} bottom={{ xs: 22 }}>
              <Margin bottom={{ xs: 8 }}>
                <Field icon="mail">
                  <input
                    onChange={handleChange('username')}
                    type="text"
                    value={form.username}
                    placeholder={t('forms.emailPlaceholder')}
                  />
                </Field>
              </Margin>
              <Margin bottom={{ xs: 16 }}>
                <Field icon="lock">
                  <Password
                    onChange={handleChange('password')}
                    value={form.password}
                    placeholder={t('forms.passwordPlaceholder')}
                  />
                </Field>
              </Margin>
              <Margin bottom={{ xs: 16 }}>
                <Row>
                  <Col size={{ md: 1 / 3 }}>
                    <Checkbox selected={form.remember} onChange={handleChangeCustom('remember')}>
                      {t('forms.remember')}
                    </Checkbox>
                  </Col>
                </Row>
              </Margin>
              <Row>
                <Col size={{ md: 1 / 3 }}>
                  <Button type="submit">{t('auth.logIn')}</Button>
                </Col>
              </Row>
            </Margin>
          </form>
        </Col>
        <Col>
          <Margin as="p" bottom={{ xs: 12 }}>
            {t('auth.orSignIn')}
          </Margin>
          <Row>
            <Col size={{ lg: 1 / 6 }}>
              <Margin bottom={{ xs: 8 }} top={{ xs: 8 }}>
                <GoogleButton onClick={() => loginSocial('google')}>
                  <ReactSVG src={iconList.googleColor} />
                  {t('auth.sGoogle')}
                </GoogleButton>
              </Margin>
            </Col>

            <Col size={{ lg: 1 / 6 }}>
              <Margin bottom={{ xs: 8 }} top={{ xs: 8 }}>
                <MicrosoftButton onClick={() => loginSocial('microsoft')}>
                  <ReactSVG src={iconList.windows} />
                  {t('auth.sMicrosoft')}
                </MicrosoftButton>
              </Margin>
            </Col>

            <Col size={{ lg: 1 / 6 }}>
              <Margin bottom={{ xs: 8 }} top={{ xs: 8 }}>
                <FacebookButton onClick={() => loginSocial('facebook')}>
                  <ReactSVG src={iconList.facebookSquare} />
                  {t('auth.sFacebook')}
                </FacebookButton>
              </Margin>
            </Col>

            <Col size={{ lg: 1 / 6 }}>
              <Margin bottom={{ xs: 8 }} top={{ xs: 8 }}>
                <LinkedinButton onClick={() => loginSocial('linkedin')}>
                  <ReactSVG src={iconList.linkedin} />
                  {t('auth.sLinkedin')}
                </LinkedinButton>
              </Margin>
            </Col>

            <Col size={{ lg: 1 / 6 }}>
              <Margin bottom={{ xs: 8 }} top={{ xs: 8 }}>
                <TwitterButton onClick={() => loginSocial('twitter')}>
                  <ReactSVG src={iconList.twitter} />
                  {t('auth.sTwitter')}
                </TwitterButton>
              </Margin>
            </Col>
            <Col size={{ lg: 1 / 6 }}>
              <Margin bottom={{ xs: 8 }} top={{ xs: 8 }}>
                <AppleButton onClick={() => loginSocial('apple')}>
                  <ReactSVG src={iconList.apple} />
                  {t('auth.sApple')}
                </AppleButton>
              </Margin>
            </Col>
          </Row>
          <Link to="/auth/password" className="link uppercase">
            {t('auth.forgotPassword')}?
          </Link>
        </Col>
      </Row>
    </div>
  );
}

Login.propTypes = {
  loginUser: PropTypes.func.isRequired,
  setNewAlert: PropTypes.func.isRequired,
  loginSocial: PropTypes.func.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  loginUser: (payload, history, ipmaShopVerify) =>
    dispatch(loginUserCreator(payload, history, ipmaShopVerify)),
  loginSocial: (payload) => dispatch(loginSocialCreator(payload)),
  setNewAlert: (payload) => dispatch(setNewAlertCreator(payload)),
});

export default connect(null, mapDispatchToProps)(Login);
